import Model, { attr, belongsTo } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import type District from 'district-ui-client/models/district'
import { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type StringTransform from 'district-ui-client/transforms/string'

// order of preference of subscription-types
const subscriptionOrder = [SubscriptionType.Reading, SubscriptionType.Maths, SubscriptionType.Mathletics]

const blakeLocaleCountryToLanguageMap: Record<string, string> = {
  au: 'en-AU',
  ca: 'en-CA',
  cn: 'zh',
  in: 'en-GB',
  int: 'en',
  kr: 'ko',
  nz: 'en-NZ',
  th: 'th',
  uk: 'en-GB',
  gb: 'en-GB',
  us: 'en-US',
  za: 'en-ZA',
}

// copied from frontend monorepo site-copy addon
function languageFromBlakeData(blakeCountryCode?: string) {
  const countryCode = blakeCountryCode?.toLowerCase()

  const languageKey = countryCode || 'int'

  return blakeLocaleCountryToLanguageMap[languageKey] ?? 'en'
}

// Not an exhaustive list.
export const countryCodeNames: Record<string, string> = {
  AU: 'Australia',
  US: 'United States',
  CA: 'Canada',
  NZ: 'New Zealand',
  ZA: 'South Africa',
  GB: 'United Kingdom',
}

export default class DistrictCoordinator extends Model {
  @attr<StringTransform>('string') declare login: string

  @attr<StringTransform>('string') declare firstName: string

  @attr<StringTransform>('string') declare lastName: string

  @attr<StringTransform>('string') declare email: string

  /**
   * AU, GB, US, ... ISO_3166-1 alpha-2 codes https://en.wikipedia.org/wiki/ISO_3166-1
   */
  @attr<StringTransform>('string') declare countryCode: string

  /**
   * An array of the subscription types this disco is allowed to manage
   */
  @attr() declare private products: SubscriptionType[]

  @belongsTo<District>('district', { async: false, inverse: null }) declare district: District

  get fullName() {
    return `${this.firstName} ${this.lastName}`
  }

  get language() {
    return languageFromBlakeData(this.countryCode)
  }

  /**
   * Display name for a country code. May not have them all.
   */
  get countryName(): string | undefined {
    return countryCodeNames[this.countryCode]
  }

  get subscriptionTypes() {
    return [...this.products].sort((a, b) => subscriptionOrder.indexOf(a) - subscriptionOrder.indexOf(b))
  }

  set subscriptionTypes(value) {
    this.products = value
  }

  [Type] = 'district-coordinator' as const
}
